import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const WhatsAppInboxContext = createContext(null);
export const useWhatsAppInbox = () => useContext(WhatsAppInboxContext) || { total: 0, byLead: {}, needsReply: 0, refresh: () => {}, markLeadRead: () => {} };

const POLL_MS = 30_000;

function playBeep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.type = "sine";
    o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.32);
    o.start();
    o.stop(ctx.currentTime + 0.34);
    o.onended = () => ctx.close();
  } catch {
    /* no-op */
  }
}

function desktopNotify(title, body) {
  try {
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    const n = new Notification(title, { body, tag: "meda-wa", icon: "/logo192.png" });
    n.onclick = () => { window.focus(); n.close(); };
  } catch {
    /* no-op */
  }
}

export function WhatsAppInboxProvider({ children }) {
  const { user } = useAuth();
  const [total, setTotal] = useState(0);
  const [byLead, setByLead] = useState({});
  const [needsReply, setNeedsReply] = useState(0);
  const seen = useRef(new Set());
  const primed = useRef(false);

  const markTemp = useCallback(async (leadId, temperature) => {
    try {
      await api.post(`/admin/leads/${leadId}/wa-reply`, { temperature, replied: true });
      toast.success(`Marked ${temperature}`);
    } catch {
      toast.error("Could not update");
    }
  }, []);

  const handleNew = useCallback((recent) => {
    for (const m of recent) {
      if (seen.current.has(m.id)) continue;
      seen.current.add(m.id);
      if (!primed.current) continue; // skip the initial batch (pre-existing unread)
      playBeep();
      desktopNotify(`💬 ${m.lead_name}`, m.preview);
      toast.custom(
        (id) => (
          <div className="w-[340px] rounded-xl border border-[#25D366]/40 bg-white p-4 shadow-lg" data-testid={`wa-inbox-toast-${m.lead_id}`}>
            <div className="text-xs font-semibold uppercase tracking-wide text-[#1aa851]">New WhatsApp message</div>
            <div className="mt-1 text-sm font-medium text-[#0A2540]">{m.lead_name}</div>
            <div className="mt-0.5 truncate text-xs text-slate-500">“{m.preview}”</div>
            <div className="mt-3 flex gap-2">
              <a
                href={`/admin/whatsapp?lead=${m.lead_id}`}
                onClick={() => toast.dismiss(id)}
                className="flex-1 rounded-md bg-[#0A2540] px-2 py-1.5 text-center text-xs font-medium text-white hover:bg-[#1E3A5F]"
                data-testid={`wa-inbox-open-${m.lead_id}`}
              >Open chat</a>
              <button
                onClick={() => { markTemp(m.lead_id, "Hot"); toast.dismiss(id); }}
                className="rounded-md bg-rose-600 px-2 py-1.5 text-xs font-medium text-white hover:bg-rose-700"
                data-testid={`wa-inbox-hot-${m.lead_id}`}
              >🔥 Hot</button>
              <button
                onClick={() => { markTemp(m.lead_id, "Warm"); toast.dismiss(id); }}
                className="rounded-md bg-amber-500 px-2 py-1.5 text-xs font-medium text-white hover:bg-amber-600"
                data-testid={`wa-inbox-warm-${m.lead_id}`}
              >☀️ Warm</button>
            </div>
          </div>
        ),
        { duration: 18000 },
      );
    }
  }, [markTemp]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/whatsapp/unread");
      setTotal(data.total_unread || 0);
      setByLead(data.by_lead || {});
      setNeedsReply(data.needs_reply || 0);
      handleNew(data.recent || []);
      primed.current = true;
    } catch {
      /* silent */
    }
  }, [handleNew]);

  const markLeadRead = useCallback((leadId) => {
    setByLead((b) => {
      if (!b[leadId]) return b;
      const next = { ...b };
      const dec = next[leadId];
      delete next[leadId];
      setTotal((t) => Math.max(0, t - dec));
      return next;
    });
    api.post(`/admin/leads/${leadId}/whatsapp/read`).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    refresh();
    const idt = setInterval(refresh, POLL_MS);
    return () => clearInterval(idt);
  }, [user, refresh]);

  const value = useMemo(
    () => ({ total, byLead, needsReply, refresh, markLeadRead }),
    [total, byLead, needsReply, refresh, markLeadRead],
  );

  return (
    <WhatsAppInboxContext.Provider value={value}>
      {children}
    </WhatsAppInboxContext.Provider>
  );
}
